import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { API_BASE } from '../lib/apiBase';
import { Printer, Upload, CheckCircle, PenLine, FileText } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { isMobileDevice } from '../lib/device';

/**
 * Gegenzeichnung durch den Berater — spiegelt bewusst den Kundenweg (SigningPage):
 * Seite drucken, unterschreiben, abfotografieren, hochladen. Zugang allein über den
 * Token aus der Benachrichtigungsmail, damit es auch am Handy ohne Login geht.
 *
 * Der Rücklauf wird serverseitig in den VOLLSTÄNDIGEN Bericht eingesetzt — hier wird
 * also nur eine Seite hochgeladen, abgelegt wird das ganze Dokument.
 */
interface Info {
  status: 'offen' | 'erledigt' | string;
  berichtstitel: string | null;
  kundeName: string | null;
  seiteVerfuegbar: boolean;
}

export default function GegenzeichnenPage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fertig, setFertig] = useState(false);
  // Am Rechner wird gedruckt, am Handy fotografiert — der QR-Code überbrückt den Wechsel,
  // genau wie auf der Kundenseite. Am Handy selbst wäre er sinnlos.
  const [isMobile] = useState(() => isMobileDevice());
  const [qrUrl] = useState(() => (typeof window === 'undefined' ? '' : window.location.href));

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/gegenzeichnung/${token}`)
      .then(r => { if (!r.ok) throw new Error('Link ungültig oder abgelaufen'); return r.json(); })
      .then((d: Info) => { setInfo(d); if (d.status === 'erledigt') setFertig(true); })
      .catch(e => setError(e.message));
  }, [token]);

  const seiteUrl = `${API_BASE}/api/gegenzeichnung/${token}/seite`;

  const hochladen = async (list: FileList | null) => {
    const datei = list?.[0];
    if (!datei || !token) return;
    setUploading(true);
    setUploadError(null);
    const form = new FormData();
    form.append('datei', datei);
    try {
      const res = await fetch(`${API_BASE}/api/gegenzeichnung/${token}/upload`, { method: 'POST', body: form });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setUploadError(body?.message || 'Upload fehlgeschlagen. Bitte erneut versuchen.');
        return;
      }
      setFertig(true);
    } catch {
      setUploadError('Die Verbindung wurde unterbrochen. Bitte erneut versuchen.');
    } finally {
      setUploading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <p className="text-slate-500 text-sm mt-2">Bitte öffnen Sie das Dokument in der App.</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (fertig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="bg-white rounded-xl shadow-sm border border-green-200 p-8 max-w-md w-full text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900">Gegenzeichnung ist drin</h1>
          <p className="text-slate-600 mt-2">
            Die Seite wurde in den vollständigen Bericht eingesetzt. Er liegt jetzt als
            „Bericht PDF – Vollständig unterschrieben" in der Ablage.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PenLine className="w-7 h-7 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Bericht gegenzeichnen</h1>
          <p className="text-slate-500 mt-2">
            {info.kundeName ? <><strong>{info.kundeName}</strong>{info.berichtstitel ? ` · ${info.berichtstitel}` : ''}</> : info.berichtstitel}
          </p>
          <p className="text-slate-500 mt-1 text-sm">
            Der Kunde hat unterschrieben. Es fehlt nur noch Ihre Unterschrift auf derselben Seite.
          </p>
        </div>

        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4">
          <p className="text-base font-semibold text-red-900 mb-2">So geht's</p>
          <ol className="text-base text-red-900/90 space-y-1.5 list-decimal list-inside">
            <li>Seite ausdrucken</li>
            <li className="font-bold">Mit Stift auf Papier unterschreiben</li>
            <li>Abfotografieren oder einscannen</li>
            <li>Hier wieder hochladen</li>
          </ol>
          <p className="text-base font-bold text-red-900 mt-3">
            Eine digitale Signatur akzeptiert die BAFA nicht.
          </p>
        </div>

        {!info.seiteVerfuegbar && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <p className="text-sm text-amber-800">
              Die unterschriebene Seite des Kunden wurde nicht gefunden. Bitte öffnen Sie den
              Vorgang in der App.
            </p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-4">
          <h2 className="font-semibold text-slate-900 mb-4">Schritt 1: Seite öffnen und drucken</h2>
          <a
            href={seiteUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium w-fit"
          >
            <Printer className="w-5 h-5" />
            Unterschriftsseite öffnen
          </a>
          <p className="text-xs text-slate-500 mt-2">
            Öffnet die vom Kunden unterschriebene Seite als PDF — daraus direkt drucken.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="font-semibold text-slate-900 mb-4">Schritt 2: Unterschriebene Seite hochladen</h2>

          {!isMobile && (
            <div className="flex flex-col items-center gap-3 text-center pb-5 mb-5 border-b border-slate-100">
              <div className="bg-white p-3 rounded-lg border border-slate-200">
                <QRCodeSVG value={qrUrl} size={176} />
              </div>
              <p className="text-base font-medium text-slate-800">
                QR-Code mit der Handy-Kamera scannen und ein <strong>Foto der unterschriebenen
                Seite</strong> hochladen.
              </p>
              <p className="text-xs text-slate-500">Oder unten direkt eine Datei auswählen.</p>
            </div>
          )}

          <label className="flex flex-col items-center gap-3 border-2 border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:border-blue-400 hover:bg-slate-50 transition-colors">
            {uploading
              ? <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
              : <Upload className="w-8 h-8 text-slate-400" />}
            <span className="text-sm text-slate-600 text-center">
              {uploading ? 'Wird eingesetzt…' : 'Foto oder PDF auswählen'}
              <span className="block text-xs text-slate-500 mt-1">
                Am Handy: einfach abfotografieren
              </span>
            </span>
            <input
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              disabled={uploading}
              onChange={e => { hochladen(e.target.files); e.target.value = ''; }}
            />
          </label>

          {uploadError && (
            <div className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3">
              <p className="text-sm font-semibold text-red-800">Upload nicht möglich</p>
              <p className="text-sm text-red-700 mt-1">{uploadError}</p>
            </div>
          )}

          <p className="flex items-start gap-2 text-xs text-slate-500 mt-4">
            <FileText className="w-4 h-4 shrink-0 mt-0.5" />
            Sie laden nur diese eine Seite hoch — der vollständige Bericht wird daraus
            automatisch zusammengesetzt.
          </p>
        </div>
      </div>
    </div>
  );
}
